import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { APP_ROUTES_TOKEN, AppRoute, PipelineService } from '@web-client/shared';

@Component({
  selector: 'lib-tool-pipeline',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule],
  template: `
    <div *ngIf="compatibleTools.length > 0" 
         class="mt-12 p-8 rounded-[2.5rem] border-2 border-dashed transition-all duration-500"
         style="background-color: var(--mat-sys-surface-container-low); border-color: var(--mat-sys-outline-variant)">
      <div class="flex items-center gap-4 mb-6">
        <div class="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-lg">
          <mat-icon>auto_fix_high</mat-icon>
        </div>
        <div class="flex flex-col text-left">
          <h3 class="text-xl font-black">Next Steps</h3>
          <p class="text-sm opacity-60">Pass this result to another tool for further processing.</p>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <button *ngFor="let tool of compatibleTools" 
                mat-stroked-button 
                (click)="navigateToTool(tool.path)"
                class="!h-20 !rounded-2xl !px-6 !text-left group hover:!border-indigo-600 transition-colors">
          <div class="flex items-center gap-4 w-full">
            <mat-icon class="text-indigo-600 group-hover:scale-110 transition-transform">{{ tool.icon }}</mat-icon>
            <div class="flex flex-col">
              <span class="font-bold">{{ tool.title }}</span>
              <span class="text-[10px] opacity-50 uppercase tracking-widest">Compatible</span>
            </div>
            <mat-icon class="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">arrow_forward</mat-icon>
          </div>
        </button>
      </div>
    </div>
  `
})
export class ToolPipelineComponent {
  private readonly APP_ROUTE = inject(APP_ROUTES_TOKEN)
  private pipelineService = inject(PipelineService);
  private router = inject(Router);

  private _outputType?: string;

  @Input() set outputType(type: string | undefined) {
    this._outputType = type;
    if (type) {
      this.compatibleTools = this.getCompatibleTools(type);
    }
  }

  get outputType(): string | undefined {
    return this._outputType;
  }

  @Input() data: any; // The actual content to pass

  compatibleTools: any[] = [];

  navigateToTool(path: string) {
    if (this.data) {
      this.pipelineService.transferData({
        type: this.outputType || 'unknown',
        content: this.data,
        sourceTool: this.router.url
      });
    }
    this.router.navigate([path]);
  }


  /**
   * Finds all tools that can accept a specific output type.
   */
  getCompatibleTools(outputType: string): AppRoute[] {
    const compatible: AppRoute[] = [];
    const currentUrl = this.router.url.split('?')[0];

    const traverse = (routes: AppRoute[]) => {
      routes.forEach(r => {
        if (r.inputs?.includes(outputType) && !r.comingSoon && r.path !== currentUrl) {
          compatible.push(r);
        }
        if (r.children) {
          traverse(r.children);
        }
      });
    };

    traverse(this.APP_ROUTE);
    return compatible;
  }
}
